import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Title,
  Box,
  Flex,
  TableProps,
  ActionIcon,
  Badge,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from 'mantine-react-table';
import { TFunction } from 'i18next';
import { useQueryParams, StringParam } from 'use-query-params';
import { getDefaultTableOptions } from '../../../components/MRT/ManifestMRT';
import BasicSearch from '../../../components/BasicSearch';
import DataTableFooter from '../../../components/DataTables/DataTableFooter';
import ClickableRegion from '../../../components/ClickableRegion';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { useTableSorting } from '../../../hooks/utils/useTableSorting';
import { Helmet } from 'react-helmet-async';
import { useLicenses } from './hooks/useLicenses';
import { LicenseData } from './types/licenseData.interface';
import Icon from '../../../components/Icon';
import { UpdateLicenseModal } from './components/UpdateLicenseModal';
import styles from './Licenses.module.scss';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { useFetchUserHasPermissions } from '../../../api/user/useFetchUserHasPermissions';

const getColumns = (
  t: TFunction,
  onEditClick: (license: LicenseData) => void,
  canUpdateLicense: boolean,
): MRT_ColumnDef<LicenseData>[] => {
  return [
    {
      accessorKey: 'fullName',
      header: t('page.userSettings.licenses.columns.name'),
      enableSorting: true,
      sortingFn: 'alphanumeric',
      Cell: ({ row }) => <span>{row.original.fullName || row.original.shortName}</span>,
    },
    {
      accessorKey: 'shortName',
      header: t('page.userSettings.licenses.columns.licenseId'),
      enableSorting: true,
      sortingFn: 'alphanumeric',
      Cell: ({ row }) => (
        <Flex align="center" gap="xs">
          <span>{row.original.shortName}</span>
          {row.original.licenseUrl && (
            <ClickableRegion
              regionLabel={t('page.userSettings.licenses.table.headers.licenseUrl')}
              target="_blank"
              href={row.original.licenseUrl}
            >
              <ActionIcon variant="subtle">
                <Icon
                  icon="external-link"
                  size="1x"
                  classNames={styles.externalLinkIcon}
                />
              </ActionIcon>
            </ClickableRegion>
          )}
        </Flex>
      ),
    },
    {
      accessorKey: 'licenseType',
      header: t('page.userSettings.licenses.columns.type'),
      enableSorting: true,
      sortingFn: 'alphanumeric',
      Cell: ({ row }) => (
        <span
          style={{
            color: row.original.licenseType === 'Strong Copyleft' ? 'red' : 'inherit',
          }}
        >
          {row.original.licenseType}
        </span>
      ),
    },
    {
      accessorKey: 'alertStatus',
      header: t('page.userSettings.licenses.columns.alert'),
      enableSorting: true,
      sortingFn: 'alphanumeric',
      Cell: ({ row }) => {
        const alertText = row.original.alertStatus;
        let badgeClass;
        let iconName: 'circle-check' | 'eye' | 'ban';

        switch (alertText) {
          case 'allowed':
            badgeClass = styles.badgeAllowed;
            iconName = 'circle-check';
            break;
          case 'review':
            badgeClass = styles.badgeReview;
            iconName = 'eye';
            break;
          case 'denied':
            badgeClass = styles.badgeForbidden;
            iconName = 'ban';
            break;
          default:
            badgeClass = '';
            iconName = 'eye';
        }

        return (
          <Flex align="center" justify="space-between" gap="xs">
            <Badge
              className={badgeClass}
              variant="filled"
              leftSection={<Icon icon={iconName} size="1x" />}
            >
              {alertText === 'allowed'
                ? t('page.userSettings.licenses.status.approved')
                : alertText === 'denied'
                  ? t('page.userSettings.licenses.status.forbidden')
                  : t('page.userSettings.licenses.status.review')}
            </Badge>
            <ActionIcon
              disabled={!canUpdateLicense}
              variant="subtle"
              size="sm"
              onClick={() => onEditClick(row.original)}
              aria-label="Edit license status"
              mr={12}
            >
              <Icon icon="pencil" size="1x" />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];
};

export const LicensesPage: React.FC = () => {
  const { t } = useTranslation();
  const defaultTableOptions = getDefaultTableOptions<LicenseData>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<LicenseData | null>(null);

  const { sorting, setSorting } = useTableSorting({
    tableId: 'licenses',
    defaultSortColumn: 'fullName',
    defaultSortDirection: 'asc',
  });

  const [query, setQuery] = useQueryParams({ search: StringParam });
  const [searchQuery, setSearchQuery] = useState<string>(query.search || '');

  useEffect(() => {
    if (query.search !== searchQuery) {
      setQuery({ search: searchQuery || undefined });
    }
  }, [searchQuery, setQuery]);

  useEffect(() => {
    if (query.search !== searchQuery) {
      setSearchQuery(query.search || '');
    }
  }, [query.search]);

  const { data, isLoading, isFetching } = useLicenses();
  const canUpdateLicense = useFetchUserHasPermissions({
    requiredPermissions: ['update:license'],
  });

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleEditClick = (license: LicenseData) => {
    setSelectedLicense(license);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedLicense(null);
  };

  const columns = useMemo(
    () => getColumns(t, handleEditClick, canUpdateLicense),
    [t, canUpdateLicense],
  );

  const table = useMantineReactTable<LicenseData>({
    ...defaultTableOptions,
    mantineTableProps: { 'data-testid': 'licenses-table' } as TableProps,
    mantinePaperProps: { className: 'manifest-data-table-no-footer' },
    columns,
    data: data?.data || [],
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enableGlobalFilterModes: true,
    enableSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: 'includesString',
    initialState: {
      ...defaultTableOptions.initialState,
      showGlobalFilter: true,
      sorting,
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    state: {
      globalFilter: searchQuery,
      sorting,
      isLoading,
      showLoadingOverlay: isFetching,
    },
    onGlobalFilterChange: setSearchQuery,
    onSortingChange: setSorting,
    renderTopToolbar: () => (
      <Flex justify="space-between" m="1rem 0">
        <Flex gap="xs">
          <Box miw="260px">
            <BasicSearch
              initialValue={searchQuery}
              handleSearch={handleSearchChange}
              placeholder={t('page.userSettings.licenses.search')}
            />
          </Box>
        </Flex>
      </Flex>
    ),
  });

  const totalCount = table?.getPrePaginationRowModel()?.rows?.length;

  return (
    <>
      <Helmet title={t('page.userSettings.licenses.page-title')}>
        <meta name="description" content={t('app.oneliner')} />
      </Helmet>
      <section className="page-licenses anim-slideInUpShort">
        <Container size="xl" py="md">
          <Title order={1} mb="xs" className="anim-slideInDownShort">
            {t('page.userSettings.licenses.title')}
          </Title>
          <TextComponent color="muted" mb="lg">
            {t('page.userSettings.licenses.totalCount', { count: totalCount })}
          </TextComponent>
          <div className="list-licenses">
            <MantineReactTable table={table} />
            <DataTableFooter
              currentPage={table.getState().pagination.pageIndex}
              limitPerPage={table.getState().pagination.pageSize}
              totalResults={totalCount}
              onChangePage={({
                pageIndex,
                pageSize,
              }: {
                pageIndex: number;
                pageSize: number;
              }) => {
                table.setPageIndex(pageIndex);
                table.setPageSize(pageSize);
              }}
            />
          </div>
        </Container>
      </section>
      <UpdateLicenseModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        license={selectedLicense}
      />
    </>
  );
};

export default LicensesPage;
